/**
 * Draw Visual Charting v1 (PRD.168).
 *
 * Shared drawing component supporting three creation modes:
 *   1. Blank canvas (white background)
 *   2. Draw on image (encounter image as base)
 *   3. Draw on template (preset template image)
 *
 * Uses Konva / react-konva with a base-layer + drawing-layer model.
 * Persists structured drawing data (JSON strokes) as the source of truth.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Line, Text, Image as KonvaImage, Rect, Group } from 'react-konva';
import Konva from 'konva';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qPatientFilesByEncounter,
  qEncounterVisualCharts,
  qChartingTemplates,
  qPatientById,
  type PatientFileRow,
  type PatientDetailRow,
  type EncounterVisualChartRow,
  type ChartingTemplateRow,
} from '@helios/data-access';
import { generateThumbnail, generateStrokesOverlay } from './SavedDrawPreview';

// ─── Types ───

type BaseType = 'blank' | 'image' | 'template';
type Tool = 'pen' | 'eraser' | 'text';

interface StrokeData {
  tool: 'pen' | 'eraser';
  points: number[];
  color: string;
  strokeWidth: number;
}

interface TextAnnotation {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  fontSize: number;
}

export interface DrawingRecord {
  baseType: BaseType;
  baseImageId: string | null;
  templateId: string | null;
  strokes: StrokeData[];
  texts?: TextAnnotation[];
  canvasWidth: number;
  canvasHeight: number;
}

// ─── Template overlay types (read-only rendering in Draw) ───

interface TemplateOverlay {
  id: string;
  type: 'text' | 'field';
  x: number;
  y: number;
  text?: string;
  fontSize?: number;
  label?: string;
  width?: number;
  height?: number;
  bindingKey?: string;
}

function parseTemplateOverlays(config: string | null | undefined): TemplateOverlay[] {
  if (!config) return [];
  try {
    const parsed = JSON.parse(config);
    return Array.isArray(parsed.overlays) ? parsed.overlays : [];
  } catch {
    return [];
  }
}

/** Resolve a field binding key to actual patient/encounter data. */
function resolveFieldBinding(
  bindingKey: string | undefined,
  label: string | undefined,
  patient: PatientDetailRow | null,
): string {
  if (!bindingKey || !patient) return label ?? 'Field';
  switch (bindingKey) {
    case 'patient_name':
      return patient.full_name ?? '';
    case 'patient_dob':
      return patient.birth_date ? new Date(patient.birth_date).toLocaleDateString() : '';
    case 'patient_age':
      if (!patient.birth_date) return '';
      return String(
        Math.floor(
          (Date.now() - new Date(patient.birth_date).getTime()) / (365.25 * 24 * 60 * 60 * 1000),
        ),
      );
    case 'patient_gender':
      return patient.sex ?? '';
    case 'visit_date':
      return new Date().toLocaleDateString();
    case 'provider_name':
      return patient.assigned_doctor ?? '';
    case 'custom':
      return label ?? '';
    default:
      return label ?? bindingKey;
  }
}

// ─── Constants ───

const CANVAS_ASPECT = 4 / 3; // width / height
const COLORS = ['#000000', '#e53e3e', '#3182ce', '#38a169', '#d69e2e', '#805ad5'];
const STROKE_WIDTHS = [2, 4, 8];

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  /** Open a specific drawing record (e.g. from clicking a card in All). */
  initialChartId?: string | null;
}

// ─── Component ───

export function DrawCharting({ patientId, encounterId, visitId, initialChartId }: Props) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  // ─── Drawing chart records ───
  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId, 'drawing'),
  );

  // ─── Encounter images for "draw on image" ───
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );
  const images = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);

  // ─── Patient data for field binding resolution (PRD.173) ───
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const patient = patientRows[0] ?? null;

  // ─── Saved charting templates (PRD.173) ───
  const { data: savedTemplates = [] } = usePowerSyncQuery<ChartingTemplateRow>(
    qChartingTemplates(orgId),
  );

  // ─── Mode selection state ───
  const [phase, setPhase] = useState<'pick' | 'draw'>('pick');
  const [baseType, setBaseType] = useState<BaseType>('blank');
  const [baseImageUrl, setBaseImageUrl] = useState<string | null>(null);
  const [baseImageId, setBaseImageId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateOverlays, setTemplateOverlays] = useState<TemplateOverlay[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // ─── Responsive canvas sizing ───
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(600);
  // Use image aspect ratio when available, otherwise default 4:3
  const [loadedImageNatural, setLoadedImageNatural] = useState<{ w: number; h: number } | null>(
    null,
  );
  const canvasHeight = loadedImageNatural
    ? Math.round(canvasWidth * (loadedImageNatural.h / loadedImageNatural.w))
    : Math.round(canvasWidth / CANVAS_ASPECT);

  useEffect(() => {
    const el = canvasContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setCanvasWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase]); // re-observe when switching to draw phase

  // ─── Drawing state ───
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [strokes, setStrokes] = useState<StrokeData[]>([]);
  const [textAnnotations, setTextAnnotations] = useState<TextAnnotation[]>([]);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [undoneStrokes, setUndoneStrokes] = useState<StrokeData[]>([]);
  const isDrawing = useRef(false);
  const stageRef = useRef<Konva.Stage | null>(null);

  // ─── Loaded base image (for Konva) ───
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  // Computed "object-fit: contain" rect for the base image within the canvas.
  const [imgRect, setImgRect] = useState({ x: 0, y: 0, w: canvasWidth, h: canvasHeight });
  useEffect(() => {
    const url = baseImageUrl;
    if (!url) {
      setLoadedImage(null);
      setLoadedImageNatural(null);
      setImgRect({ x: 0, y: 0, w: canvasWidth, h: canvasHeight });
      return;
    }
    const img = new window.Image();
    // Skip crossOrigin for Firebase Storage URLs — they don't serve CORS headers
    // for localhost. Regular <canvas> rendering works fine without it; only
    // toDataURL() would need CORS (thumbnails use a separate offscreen stage).
    if (!url.includes('firebasestorage.googleapis.com')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      setLoadedImageNatural({ w: img.naturalWidth, h: img.naturalHeight });
      // Canvas height adapts to image aspect ratio, so image fills full canvas
      setImgRect({
        x: 0,
        y: 0,
        w: canvasWidth,
        h: Math.round(canvasWidth * (img.naturalHeight / img.naturalWidth)),
      });
      setLoadedImage(img);
    };
    img.src = url;
  }, [baseType, baseImageUrl, templateId, canvasWidth, canvasHeight]);

  // ─── Persistence: load existing chart ───
  const [selectedChartId, setSelectedChartId] = useState<string | null>(initialChartId ?? null);
  const persistedIdRef = useRef<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const hydratedRef = useRef<string | null>(null);

  useEffect(() => {
    // Only hydrate when a specific chart is selected (via initialChartId or user click).
    // Do NOT auto-fallback to chartRows[0] — the picker should be shown instead
    // so users can choose between existing drawings or create a new one (PRD.170 §5.3).
    if (!selectedChartId) return;
    const chart = chartRows.find((c) => c.id === selectedChartId) ?? null;
    if (!chart || hydratedRef.current === chart.id) return;

    try {
      const record: DrawingRecord = chart.drawing_data ? JSON.parse(chart.drawing_data) : null;
      if (record) {
        setBaseType(record.baseType);
        setBaseImageId(record.baseImageId);
        setTemplateId(record.templateId);
        setStrokes(record.strokes);
        setTextAnnotations(record.texts ?? []);
        setUndoneStrokes([]);
        // resolve base image URL
        if (record.baseType === 'image' && record.baseImageId) {
          const file = fileRows.find((f) => f.id === record.baseImageId);
          setBaseImageUrl(file?.url ?? null);
        } else if (record.baseType === 'template' && record.templateId) {
          // Resolve template image URL and overlays from saved templates.
          // If templates haven't loaded yet, skip — the effect will re-run
          // when savedTemplates arrives (don't mark as hydrated).
          const tpl = savedTemplates.find((t) => t.id === record.templateId);
          if (!tpl) return; // wait for templates to load
          setBaseImageUrl(tpl.image_url ?? null);
          setTemplateOverlays(parseTemplateOverlays(tpl.config));
        } else {
          setBaseImageUrl(null);
        }
        setPhase('draw');
        persistedIdRef.current = chart.id;
        hydratedRef.current = chart.id;
        setTouched(false);
      }
    } catch {
      // malformed data — ignore
    }
  }, [chartRows, selectedChartId, fileRows, savedTemplates]);

  // ─── Start drawing from mode picker ───
  const startBlank = () => {
    setBaseType('blank');
    setBaseImageUrl(null);
    setBaseImageId(null);
    setTemplateId(null);
    resetDrawing();
    setPhase('draw');
  };

  const startFromImage = (file: PatientFileRow) => {
    setBaseType('image');
    setBaseImageUrl(file.url);
    setBaseImageId(file.id);
    setTemplateId(null);
    resetDrawing();
    setPhase('draw');
  };

  const startFromTemplate = (tpl: ChartingTemplateRow) => {
    setBaseType('template');
    setBaseImageUrl(tpl.image_url);
    setBaseImageId(null);
    setTemplateId(tpl.id);
    setTemplateOverlays(parseTemplateOverlays(tpl.config));
    resetDrawing();
    setPhase('draw');
  };

  const resetDrawing = () => {
    setStrokes([]);
    setTextAnnotations([]);
    setSelectedTextId(null);
    setEditingTextId(null);
    setUndoneStrokes([]);
    persistedIdRef.current = null;
    hydratedRef.current = null;
    setTouched(false);
    setSelectedChartId(null);
  };

  // ─── Drawing handlers ───
  const getPointerPos = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return null;
    return stage.getPointerPosition();
  }, []);

  const handleMouseDown = useCallback(() => {
    if (tool === 'text') {
      if (selectedTextId) {
        // Click canvas while text selected → deselect
        setSelectedTextId(null);
        return;
      }
      // Place new text annotation
      const pos = getPointerPos();
      if (!pos) return;
      const id = crypto.randomUUID().slice(0, 8);
      setTextAnnotations((prev) => [
        ...prev,
        { id, x: pos.x, y: pos.y, text: 'Text', color, fontSize: 16 },
      ]);
      setSelectedTextId(id);
      setEditingTextId(id);
      setTouched(true);
      return;
    }
    // Deselect any text when drawing with pen/eraser
    if (selectedTextId) setSelectedTextId(null);
    isDrawing.current = true;
    const pos = getPointerPos();
    if (!pos) return;
    setStrokes((prev) => [...prev, { tool, points: [pos.x, pos.y], color, strokeWidth }]);
    setUndoneStrokes([]);
    setTouched(true);
  }, [tool, color, strokeWidth, getPointerPos, selectedTextId]);

  const handleMouseMove = useCallback(() => {
    if (!isDrawing.current) return;
    const pos = getPointerPos();
    if (!pos) return;
    setStrokes((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      const updated = { ...last, points: [...last.points, pos.x, pos.y] };
      return [...prev.slice(0, -1), updated];
    });
  }, [getPointerPos]);

  const handleMouseUp = useCallback(() => {
    isDrawing.current = false;
  }, []);

  // ─── Undo / Redo / Clear ───
  const undo = () => {
    setStrokes((prev) => {
      if (prev.length === 0) return prev;
      setUndoneStrokes((u) => [prev[prev.length - 1], ...u]);
      setTouched(true);
      return prev.slice(0, -1);
    });
  };

  const redo = () => {
    setUndoneStrokes((prev) => {
      if (prev.length === 0) return prev;
      setStrokes((s) => [...s, prev[0]]);
      setTouched(true);
      return prev.slice(1);
    });
  };

  const clearAll = () => {
    setStrokes([]);
    setUndoneStrokes([]);
    setTouched(true);
  };

  // ─── Save ───
  const handleSave = async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      const record: DrawingRecord = {
        baseType,
        baseImageId,
        templateId,
        strokes,
        texts: textAnnotations.length > 0 ? textAnnotations : undefined,
        canvasWidth: canvasWidth,
        canvasHeight: canvasHeight,
      };
      const drawingData = JSON.stringify(record);
      const now = new Date().toISOString();
      const existingId = persistedIdRef.current;

      if (existingId) {
        await db.execute(
          `UPDATE encounter_visual_charts
             SET drawing_data = ?, base_type = ?, base_image_id = ?, template_id = ?, updated_at = ?
           WHERE id = ?`,
          [drawingData, baseType, baseImageId, templateId, now, existingId],
        );
      } else {
        const id = crypto.randomUUID();
        persistedIdRef.current = id;
        await db.execute(
          `INSERT INTO encounter_visual_charts
            (id, org_id, clinic_id, patient_id, encounter_id, visit_id, chart_type,
             base_type, base_image_id, template_id, drawing_data,
             created_by_user_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            encounterId,
            visitId || null,
            'drawing',
            baseType,
            baseImageId,
            templateId,
            drawingData,
            null,
            now,
            now,
          ],
        );
      }
      setTouched(false);
    } catch (err) {
      console.error('[DrawCharting] save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render: mode picker ───
  if (phase === 'pick') {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        {/* Saved drawings strip — thumbnail cards matching All view */}
        {chartRows.length > 0 && (
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <div style={sectionLabelStyle}>Saved Drawings</div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
              {chartRows.map((chart) => {
                let record: DrawingRecord | null = null;
                try {
                  record = chart.drawing_data ? JSON.parse(chart.drawing_data) : null;
                } catch {
                  /* skip malformed */
                }

                let baseImageUrl: string | null = null;
                if (record?.baseType === 'image' && record.baseImageId) {
                  baseImageUrl = images.find((f) => f.id === record!.baseImageId)?.url ?? null;
                } else if (record?.baseType === 'template' && record.templateId) {
                  baseImageUrl =
                    savedTemplates.find((t) => t.id === record!.templateId)?.image_url ?? null;
                }

                return (
                  <SavedDrawingThumb
                    key={chart.id}
                    record={record}
                    baseImageUrl={baseImageUrl}
                    baseType={chart.base_type ?? 'blank'}
                    onClick={() => {
                      setSelectedChartId(chart.id);
                      hydratedRef.current = null;
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        <div style={sectionLabelStyle}>Start New Drawing</div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          {/* Blank canvas */}
          <ModeCard
            icon="edit"
            label="Blank Canvas"
            description="Draw on a white background"
            onClick={startBlank}
          />

          {/* From image */}
          <ModeCard
            icon="camera"
            label="From Image"
            description={
              images.length > 0
                ? `${images.length} image${images.length > 1 ? 's' : ''} available`
                : 'No images yet'
            }
            onClick={() => {
              if (images.length > 0) setShowImagePicker(true);
            }}
            disabled={images.length === 0}
          />

          {/* From template */}
          <ModeCard
            icon="layout-grid"
            label="From Template"
            description={
              savedTemplates.length > 0
                ? `${savedTemplates.length} template${savedTemplates.length > 1 ? 's' : ''}`
                : 'No templates yet'
            }
            onClick={() => {
              if (savedTemplates.length > 0) setShowTemplatePicker(true);
            }}
            disabled={savedTemplates.length === 0}
          />
        </div>

        {/* Image picker modal */}
        {showImagePicker && (
          <ImagePickerModal
            images={images}
            onPick={(file) => {
              setShowImagePicker(false);
              startFromImage(file);
            }}
            onClose={() => setShowImagePicker(false)}
          />
        )}

        {/* Template picker modal */}
        {showTemplatePicker && (
          <TemplatePickerModal
            templates={savedTemplates}
            onPick={(tpl) => {
              setShowTemplatePicker(false);
              startFromTemplate(tpl);
            }}
            onClose={() => setShowTemplatePicker(false)}
          />
        )}
      </div>
    );
  }

  // ─── Render: drawing surface ───
  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--sp-3)',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        {/* Left: back + tools */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ToolbarBtn
            title="Back"
            onClick={() => {
              setPhase('pick');
              resetDrawing();
            }}
          >
            <NavIcon name="chevron-left" />
          </ToolbarBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />

          <ToolbarBtn
            title="Pen"
            active={tool === 'pen'}
            onClick={() => {
              setTool('pen');
              setSelectedTextId(null);
            }}
          >
            <NavIcon name="edit" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Eraser"
            active={tool === 'eraser'}
            onClick={() => {
              setTool('eraser');
              setSelectedTextId(null);
            }}
          >
            <NavIcon name="eraser" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Text"
            active={tool === 'text'}
            onClick={() => {
              setTool('text');
              setSelectedTextId(null);
            }}
          >
            T
          </ToolbarBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />

          {/* Color swatches */}
          {COLORS.map((c) => (
            <button
              key={c}
              title={c}
              onClick={() => setColor(c)}
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: c,
                border: color === c ? '2px solid var(--color-primary)' : '1px solid var(--border)',
                cursor: 'pointer',
                padding: 0,
                outline:
                  color === c
                    ? '2px solid var(--color-primary-light, rgba(59,130,246,0.3))'
                    : 'none',
                outlineOffset: 1,
              }}
            />
          ))}

          <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />

          {/* Stroke width */}
          {STROKE_WIDTHS.map((w) => (
            <button
              key={w}
              title={`${w}px`}
              onClick={() => setStrokeWidth(w)}
              style={{
                width: 26,
                height: 26,
                borderRadius: 'var(--radius-sm)',
                border:
                  strokeWidth === w
                    ? '1.5px solid var(--color-primary)'
                    : '1px solid var(--border)',
                background:
                  strokeWidth === w
                    ? 'var(--color-primary-bg, rgba(59,130,246,0.08))'
                    : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <div
                style={{
                  width: Math.min(w * 2, 16),
                  height: Math.min(w * 2, 16),
                  borderRadius: '50%',
                  background: 'var(--text)',
                }}
              />
            </button>
          ))}
        </div>

        {/* Right: undo/redo/clear + save */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ToolbarBtn title="Undo" onClick={undo} disabled={strokes.length === 0}>
            <NavIcon name="undo" />
          </ToolbarBtn>
          <ToolbarBtn title="Redo" onClick={redo} disabled={undoneStrokes.length === 0}>
            <NavIcon name="redo" />
          </ToolbarBtn>
          <ToolbarBtn title="Clear all" onClick={clearAll} disabled={strokes.length === 0}>
            <NavIcon name="trash" />
          </ToolbarBtn>

          <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />

          {/* Save status */}
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: touched ? 'var(--color-warning-text)' : 'var(--text-muted)',
            }}
          >
            {touched ? 'Unsaved changes' : persistedIdRef.current ? 'Saved' : ''}
          </span>

          <button
            onClick={handleSave}
            disabled={!touched || saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${!touched || saving ? 'var(--border)' : 'var(--color-primary)'}`,
              background: !touched || saving ? 'var(--bg-surface)' : 'var(--color-primary)',
              color: !touched || saving ? 'var(--text-muted)' : '#fff',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: !touched || saving ? 'default' : 'pointer',
              opacity: !touched ? 0.5 : 1,
            }}
          >
            <NavIcon name="check" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasContainerRef}
        style={{
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '1px solid var(--border)',
          background: '#fff',
          width: '100%',
          cursor:
            tool === 'eraser'
              ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Ccircle cx='12' cy='12' r='10' fill='none' stroke='%23888' stroke-width='1.5'/%3E%3Cline x1='8' y1='8' x2='16' y2='16' stroke='%23888' stroke-width='1.5' stroke-linecap='round'/%3E%3Cline x1='16' y1='8' x2='8' y2='16' stroke='%23888' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E") 12 12, crosshair`
              : 'crosshair',
        }}
      >
        <Stage
          ref={stageRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          {/* Base layer */}
          <Layer>
            <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill="#ffffff" />
            {loadedImage && (
              <KonvaImage
                image={loadedImage}
                x={imgRect.x}
                y={imgRect.y}
                width={imgRect.w}
                height={imgRect.h}
              />
            )}
          </Layer>

          {/* Template overlays layer (read-only, PRD.173 §7) */}
          {templateOverlays.length > 0 && loadedImage && (
            <Layer listening={false}>
              {templateOverlays.map((o) => {
                // Scale overlay coordinates from builder canvas to the contain-fit
                // image rect in the encounter canvas. The builder stores overlays
                // relative to the image's natural size, so we scale to imgRect
                // (which is the contain-fit area) and offset by imgRect.x/y.
                const scaleX = imgRect.w / loadedImage.naturalWidth;
                const scaleY = imgRect.h / loadedImage.naturalHeight;
                const sx = imgRect.x + o.x * scaleX;
                const sy = imgRect.y + o.y * scaleY;
                if (o.type === 'text' && o.text) {
                  return (
                    <Text
                      key={o.id}
                      x={sx}
                      y={sy}
                      text={o.text}
                      fontSize={Math.round((o.fontSize ?? 16) * scaleX)}
                      fill="#1a1a1a"
                      fontStyle="bold"
                      listening={false}
                    />
                  );
                }
                if (o.type === 'field') {
                  const sw = (o.width ?? 120) * scaleX;
                  const sh = (o.height ?? 28) * scaleY;
                  return (
                    <Group key={o.id}>
                      <Rect
                        x={sx}
                        y={sy}
                        width={sw}
                        height={sh}
                        fill="rgba(37,99,235,0.12)"
                        stroke="#2563eb"
                        strokeWidth={1}
                        dash={[4, 2]}
                        cornerRadius={3}
                        listening={false}
                      />
                      <Text
                        x={sx + 6}
                        y={sy + Math.round(sh / 4)}
                        text={resolveFieldBinding(o.bindingKey, o.label, patient)}
                        fontSize={Math.max(Math.round(12 * scaleX), 9)}
                        fill="#1e40af"
                        listening={false}
                      />
                    </Group>
                  );
                }
                return null;
              })}
            </Layer>
          )}

          {/* Drawing layer */}
          <Layer>
            {strokes.map((stroke, i) => (
              <Line
                key={i}
                points={stroke.points}
                stroke={stroke.tool === 'eraser' ? '#ffffff' : stroke.color}
                strokeWidth={stroke.tool === 'eraser' ? stroke.strokeWidth * 3 : stroke.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation={
                  stroke.tool === 'eraser' ? 'destination-out' : 'source-over'
                }
              />
            ))}
            {/* Text annotations (encounter-specific, editable) */}
            {textAnnotations.map((t) => (
              <Text
                key={t.id}
                x={t.x}
                y={t.y}
                text={t.text}
                fontSize={t.fontSize}
                fill={t.color}
                fontStyle="bold"
                draggable
                onClick={() => {
                  setSelectedTextId(t.id);
                  setTool('text');
                }}
                onTap={() => {
                  setSelectedTextId(t.id);
                  setTool('text');
                }}
                onDblClick={() => setEditingTextId(t.id)}
                onDblTap={() => setEditingTextId(t.id)}
                onDragEnd={(e) => {
                  setTextAnnotations((prev) =>
                    prev.map((a) =>
                      a.id === t.id ? { ...a, x: e.target.x(), y: e.target.y() } : a,
                    ),
                  );
                  setTouched(true);
                }}
                stroke={selectedTextId === t.id ? '#2563eb' : undefined}
                strokeWidth={selectedTextId === t.id ? 0.5 : 0}
              />
            ))}
          </Layer>
        </Stage>
      </div>

      {/* Text edit modal */}
      {editingTextId &&
        (() => {
          const t = textAnnotations.find((a) => a.id === editingTextId);
          if (!t) return null;
          return (
            <>
              <div
                onClick={() => setEditingTextId(null)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 998 }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 320,
                  background: 'var(--bg-surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                  zIndex: 999,
                  padding: 'var(--sp-5)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                    Edit Text
                  </span>
                  <button
                    onClick={() => setEditingTextId(null)}
                    style={{
                      width: 28,
                      height: 28,
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-surface)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <NavIcon name="x" />
                  </button>
                </div>
                <input
                  autoFocus
                  value={t.text}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTextAnnotations((prev) =>
                      prev.map((a) => (a.id === editingTextId ? { ...a, text: val } : a)),
                    );
                    setTouched(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setEditingTextId(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text)',
                    fontSize: 'var(--font-size-sm)',
                    outline: 'none',
                    marginBottom: 12,
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => {
                      setTextAnnotations((prev) => prev.filter((a) => a.id !== editingTextId));
                      setEditingTextId(null);
                      setSelectedTextId(null);
                      setTouched(true);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--color-danger-text, #e53e3e)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <NavIcon name="trash" /> Delete
                  </button>
                  <button
                    onClick={() => setEditingTextId(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-primary)',
                      background: 'var(--color-primary)',
                      color: '#fff',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </>
          );
        })()}
    </div>
  );
}

// ─── Sub-components ───

function ModeCard({
  icon,
  label,
  description,
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: 'var(--sp-5) var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 150ms',
      }}
    >
      <NavIcon name={icon} />
      <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{label}</span>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
        {description}
      </span>
    </button>
  );
}

function ToolbarBtn({
  children,
  onClick,
  active,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        width: 30,
        height: 30,
        borderRadius: 'var(--radius-sm)',
        border: active ? '1.5px solid var(--color-primary)' : '1px solid var(--border)',
        background: active ? 'var(--color-primary-bg, rgba(59,130,246,0.08))' : 'var(--bg-surface)',
        color: active
          ? 'var(--color-primary)'
          : disabled
            ? 'var(--text-placeholder)'
            : 'var(--text-muted)',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}

function ImagePickerModal({
  images,
  onPick,
  onClose,
}: {
  images: PatientFileRow[];
  onPick: (file: PatientFileRow) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div onClick={onClose} style={backdropStyle} />
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
            Choose an image to draw on
          </span>
          <button onClick={onClose} style={modalCloseStyle}>
            <NavIcon name="x" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4)' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
              gap: 8,
            }}
          >
            {images.map((img) => (
              <button
                key={img.id}
                onClick={() => onPick(img)}
                style={{
                  padding: 0,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-surface-2)',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  aspectRatio: '1',
                }}
              >
                <img
                  src={img.url!}
                  alt={img.filename ?? ''}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function TemplatePickerModal({
  templates,
  onPick,
  onClose,
}: {
  templates: ChartingTemplateRow[];
  onPick: (tpl: ChartingTemplateRow) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div onClick={onClose} style={backdropStyle} />
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
            Choose a template
          </span>
          <button onClick={onClose} style={modalCloseStyle}>
            <NavIcon name="x" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4)' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 12,
            }}
          >
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => onPick(tpl)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: 'var(--sp-3)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-surface)',
                  cursor: 'pointer',
                }}
              >
                {tpl.image_url ? (
                  <img
                    src={tpl.image_url}
                    alt={tpl.name}
                    style={{
                      width: '100%',
                      aspectRatio: '4 / 3',
                      objectFit: 'contain',
                      borderRadius: 'var(--radius-sm)',
                      background: '#fff',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '4 / 3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--bg-surface-2)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <NavIcon name="file-text" />
                  </div>
                )}
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500 }}>{tpl.name}</span>
                {tpl.category && (
                  <span
                    style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'capitalize' }}
                  >
                    {tpl.category}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Saved drawing thumbnail (reuses offscreen Konva generation from SavedDrawPreview) ───

function SavedDrawingThumb({
  record,
  baseImageUrl,
  baseType,
  onClick,
}: {
  record: DrawingRecord | null;
  baseImageUrl: string | null;
  baseType: string;
  onClick: () => void;
}) {
  const isFirebaseImage =
    (baseType === 'image' || baseType === 'template') &&
    !!baseImageUrl &&
    baseImageUrl.includes('firebasestorage.googleapis.com');

  const [strokesUrl, setStrokesUrl] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const generatedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!record) return;
    const key = `${baseType}:${baseImageUrl ?? ''}:${record.strokes.length}`;
    if (generatedForRef.current === key) return;
    generatedForRef.current = key;

    if (isFirebaseImage) {
      setThumbUrl(null);
      generateStrokesOverlay(record)
        .then(setStrokesUrl)
        .catch(() => {});
    } else {
      setStrokesUrl(null);
      generateThumbnail(record, baseImageUrl)
        .then(setThumbUrl)
        .catch(() => {});
    }
  }, [record, baseImageUrl, baseType, isFirebaseImage]);

  const ready = isFirebaseImage ? !!strokesUrl : !!thumbUrl;

  return (
    <button
      onClick={onClick}
      title="Open Drawing"
      style={{
        width: 80,
        height: 80,
        flexShrink: 0,
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: '#fff',
        padding: 0,
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {ready ? (
        isFirebaseImage ? (
          <>
            <img
              src={baseImageUrl!}
              alt="Base"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
            <img
              src={strokesUrl!}
              alt="Strokes"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        ) : (
          <img
            src={thumbUrl!}
            alt="Drawing"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        )
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            fontSize: 9,
          }}
        >
          <NavIcon name="edit" />
        </div>
      )}
      <span
        style={{
          position: 'absolute',
          bottom: 2,
          left: 2,
          padding: '0px 4px',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(0,0,0,0.55)',
          color: '#fff',
          fontSize: 8,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.2px',
        }}
      >
        {baseType === 'image' ? 'img' : baseType === 'template' ? 'tpl' : 'draw'}
      </span>
    </button>
  );
}

// ─── Shared styles ───

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text-muted)',
  marginBottom: 8,
};

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.3)',
  zIndex: 998,
};

const modalStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 640,
  maxWidth: '92vw',
  maxHeight: '80vh',
  background: 'var(--bg-surface)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border)',
  boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
  zIndex: 999,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const modalHeaderStyle: React.CSSProperties = {
  padding: 'var(--sp-4) var(--sp-5)',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const modalCloseStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
};
